import React, { useEffect, useState } from "react";
import Cookies from "js-cookie";
import "./DatabaseMappingPage.css";
import { API_BASE } from "../api/config";
import { useAuth } from "../context/AuthContext";

const DatabaseMappingPage = () => {
  const { user: authUser } = useAuth();
  const [mappingData, setMappingData] = useState(null);
  const [finalMappings, setFinalMappings] = useState({});
  const [loading, setLoading] = useState(false); // ⬅ Standard: Kein Laden
  const [error, setError] = useState("");
  const [hasSavedMapping, setHasSavedMapping] = useState(false);
  const [isMappingCollapsed, setIsMappingCollapsed] = useState(false);
  const [importCount, setImportCount] = useState(null); // 🔥 Speichert die Anzahl importierter Zeilen
  const [vorlagen, setVorlagen] = useState([]);          // Mapping-Vorlagen anderer Konten
  const [vorlagenHinweis, setVorlagenHinweis] = useState("");

  useEffect(() => {
    // Erst die Spalten laden, dann das gespeicherte/Standard-Mapping —
    // sonst überschreibt die Initial-Vorbelegung ein bereits geladenes Mapping
    (async () => {
      await fetchMappingData();
      await fetchSavedMapping();
    })();
    fetchVorlagen();
  }, []);

  const fetchVorlagen = async () => {
    try {
      const r = await fetch(`${API_BASE}/database/mapping_vorlagen`);
      if (!r.ok) return;
      const d = await r.json();
      setVorlagen(d.vorlagen || []);
    } catch { /* Vorlagen sind optional */ }
  };

  /* Mapping eines anderen Kontos übernehmen — nur wenn ALLE Spalten der
     Vorlage in der aktuellen CSV (und den DB-Spalten) vorhanden sind. */
  const uebernehmeVorlage = (konto) => {
    setVorlagenHinweis("");
    const vorlage = vorlagen.find((v) => v.konto === konto);
    if (!vorlage || !mappingData) return;

    const csvSet = new Set(mappingData.csvHeaders);
    const dbSet = new Set(mappingData.dbHeaders);
    const fehlendeCsv = [...new Set(Object.values(vorlage.mappings))]
      .filter((c) => c && !csvSet.has(c));
    const fehlendeDb = Object.keys(vorlage.mappings).filter((d) => !dbSet.has(d));

    if (fehlendeCsv.length > 0 || fehlendeDb.length > 0) {
      const teile = [];
      if (fehlendeCsv.length) teile.push(`CSV-Spalten fehlen in der aktuellen Datei: ${fehlendeCsv.join(", ")}`);
      if (fehlendeDb.length) teile.push(`DB-Spalten unbekannt: ${fehlendeDb.join(", ")}`);
      setVorlagenHinweis(`Mapping nicht übernommen — ${teile.join(" | ")}`);
      return;
    }

    setFinalMappings({ ...vorlage.mappings });
    setIsMappingCollapsed(false);
    setVorlagenHinweis(`Mapping von ${vorlage.label} übernommen — bitte prüfen und speichern.`);
  };

  const fetchMappingData = async () => {
    setError("");
    setLoading(true); // 🔄 Ladeanimation aktivieren
    const sessionId = localStorage.getItem("sessionId");

    if (!sessionId) {
      setError("❌ Keine Session-ID gefunden. Bitte laden Sie zuerst eine Datei hoch.");
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/database/databasemapping`, {
        method: "POST",
        headers: { "Session-ID": sessionId },
      });

      if (!response.ok) {
        const errorData = await response.json();
        setError(errorData.detail || "❌ Fehler beim Abrufen der Mappings.");
        return;
      }

      const data = await response.json();
      const filteredDbHeaders = data.dbHeaders.filter(
        (header) => !["id", "import_batch_id", "user", "timestamp"].includes(header.toLowerCase())
      );

      setMappingData({ ...data, dbHeaders: filteredDbHeaders });

      const initialMappings = {};
      filteredDbHeaders.forEach((header) => {
        initialMappings[header] = data.csvHeaders.find((csvHeader) => csvHeader === header) || "";
      });
      setFinalMappings(initialMappings);
    } catch (err) {
      setError("❌ Netzwerkfehler: Mappings konnten nicht geladen werden.");
    } finally {
      setLoading(false); // 🔄 Ladeanimation beenden
    }
  };

  const fetchSavedMapping = async () => {
    setLoading(true); // 🔄 Ladeanimation aktivieren
    const sessionId = localStorage.getItem("sessionId");

    if (!sessionId) return;

    try {
      const response = await fetch(`${API_BASE}/database/get_mapping`, {
        method: "GET",
        headers: { "Session-ID": sessionId },
      });

      if (!response.ok) {
        console.error("❌ Kein gespeichertes Mapping gefunden.");
        return;
      }

      const data = await response.json();

      if (data.default_schema) {
        // Kein gespeichertes Mapping, aber das CSV-Schema ist bekannt
        // (z. B. DKB) → Standard-Mapping vorbelegen, offen lassen, Hinweis
        setFinalMappings((prev) => ({ ...prev, ...data.mappings }));
        setIsMappingCollapsed(false);
        setVorlagenHinweis(data.message || `Standard-Mapping (${data.schema_label}) vorbelegt — bitte prüfen und speichern.`);
      } else if (data.mappings && Object.keys(data.mappings).length > 0) {
        setFinalMappings(data.mappings);
        setHasSavedMapping(true);
        setIsMappingCollapsed(true); // ✅ Tabelle einklappen, falls ein Mapping existiert
      } else {
        setIsMappingCollapsed(false);
      }
    } catch (err) {
      console.error("❌ Fehler beim Abrufen des gespeicherten Mappings:", err);
    } finally {
      setLoading(false); // 🔄 Ladeanimation beenden
    }
  };

  /* Benutzername mit Fallback-Kette ermitteln — Speichern darf NIE still
     scheitern, nur weil localStorage (z. B. in einem anderen Browser) leer
     ist. Das war die Ursache für "geht in Safari, aber nicht in Chrome". */
  const getUsername = () =>
    localStorage.getItem("username") || Cookies.get("username") || authUser?.kuerzel || "unbekannt";

  const saveMappings = async () => {
    setError("");
    setLoading(true);
    const sessionId = localStorage.getItem("sessionId");

    if (!sessionId) {
      setError("Keine Session gefunden. Bitte zuerst eine CSV-Datei hochladen.");
      setLoading(false);
      return;
    }

    const requestData = { mappings: finalMappings };

    try {
      const saveResponse = await fetch(`${API_BASE}/database/save_mapping`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Session-ID": sessionId,
        },
        body: JSON.stringify(requestData),
      });

      if (!saveResponse.ok) {
        let detail = "";
        try { const j = await saveResponse.json(); detail = j.detail || ""; } catch {}
        setError(detail || `Fehler beim Speichern des Mappings (HTTP ${saveResponse.status}).`);
        setLoading(false);
        return;
      }

      setHasSavedMapping(true);
      setIsMappingCollapsed(true);

      // **Danach die Daten in die Datenbank einfügen**
      await insertDatabaseData();
    } catch (err) {
      setError(`Netzwerkfehler beim Speichern des Mappings: ${err.message}`);
    } finally {
      setLoading(false); // 🔄 Ladeanimation beenden
    }
  };

  const insertDatabaseData = async () => {
    setError("");
    setLoading(true);
    const sessionId = localStorage.getItem("sessionId");
    const user = getUsername();

    if (!sessionId) {
      setError("Keine Session gefunden. Bitte zuerst eine CSV-Datei hochladen.");
      setLoading(false);
      return;
    }

    const requestData = { mappings: finalMappings };

    try {
      const response = await fetch(`${API_BASE}/database/databaseinsert`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Session-ID": sessionId,
          "User-Name": user,
        },
        body: JSON.stringify(requestData),
      });

      let responseData = {};
      try { responseData = await response.json(); } catch {}

      if (!response.ok) {
        setError(responseData.detail || `Fehler beim Einfügen der Daten (HTTP ${response.status}).`);
        return;
      }

      setImportCount(responseData.count); // 🔥 Anzahl der importierten Zeilen speichern

      alert(`✅ Mapping gespeichert — ${responseData.count} Datensätze erfolgreich importiert!`);
    } catch (err) {
      setError(`Netzwerkfehler beim Einfügen der Daten: ${err.message}`);
    } finally {
      setLoading(false);
    }
};

  return (
    <div className="database-mapping-page">
      {loading && (
        <div className="loading-overlay">
          <div className="loading-box">
            <div className="loading-spinner"></div>
            <p>Daten werden geladen...</p>
          </div>
        </div>
      )}

      <h1>🔄 Datenbank-Mapping</h1>

      {error && <p className="error">{error}</p>}

      {mappingData ? (
        <div className="mapping-container">
          <h2>🔗 Zuordnung der Spalten</h2>
          <p>{hasSavedMapping ? "✅ Ein gespeichertes Mapping wurde gefunden!" : "⚠️ Kein gespeichertes Mapping vorhanden."}</p>

          {vorlagen.length > 0 && (
            <div style={{ margin: "8px 0 12px", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <label style={{ fontSize: "0.85rem", fontWeight: 600 }}>Mapping übernehmen von:</label>
              <select
                defaultValue=""
                onChange={(e) => { if (e.target.value) { uebernehmeVorlage(e.target.value); e.target.value = ""; } }}
                style={{ padding: "6px 10px", borderRadius: 6 }}
              >
                <option value="">-- Konto wählen --</option>
                {vorlagen.map((v) => (
                  <option key={v.konto} value={v.konto}>{v.label}</option>
                ))}
              </select>
            </div>
          )}
          {vorlagenHinweis && (
            <p style={{
              fontSize: "0.85rem", padding: "8px 12px", borderRadius: 6,
              background: vorlagenHinweis.startsWith("Mapping nicht") ? "var(--color-error-bg, #fdecea)" : "var(--color-success-bg, #e8f5e9)",
              color: vorlagenHinweis.startsWith("Mapping nicht") ? "var(--color-error, #d32f2f)" : "var(--color-success, #2e7d32)",
            }}>{vorlagenHinweis}</p>
          )}

          {hasSavedMapping && (
            <button
              className={`toggle-mapping-button ${isMappingCollapsed ? "collapsed" : "expanded"}`}
              onClick={() => setIsMappingCollapsed(!isMappingCollapsed)}
            >
              {isMappingCollapsed ? "📂 Mapping anzeigen" : "📁 Mapping verbergen"}
              <span className="arrow">▼</span>
            </button>
          )}

          {!isMappingCollapsed && (
            <div className="mapping-table">
              <div className="mapping-header">
                <span>Datenbank-Spalte</span>
                <span>CSV-Spalte</span>
              </div>

              {mappingData.dbHeaders.map((dbHeader, index) => (
                <div key={index} className="mapping-row">
                  <span className="db-field">{dbHeader}</span>
                  <select
                    value={finalMappings[dbHeader] || ""}
                    onChange={(e) => setFinalMappings({ ...finalMappings, [dbHeader]: e.target.value })}
                  >
                    <option value="">-- CSV-Spalte auswählen --</option>
                    {mappingData.csvHeaders.map((csvHeader, idx) => (
                      <option key={idx} value={csvHeader}>
                        {csvHeader}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          )}

          <button className="save-button" onClick={saveMappings} disabled={loading}>
            {loading ? "Speichern..." : "💾 Mapping speichern & Daten importieren"}
          </button>
          {importCount !== null && (
            <p className="import-success">✅ {importCount} Datensätze erfolgreich gespeichert!</p>
          )}
        </div>
      ) : (
        <p>Keine Mapping-Daten gefunden.</p>
      )}
    </div>
  );
};

export default DatabaseMappingPage;